/* eslint-disable no-underscore-dangle */
import axios from 'axios';
import * as logger from 'loglevel';
import axiosRetry, { isRetryableError } from 'axios-retry';
import { isNotNil } from '../utils';
import { isSSR } from '@front10/utils';
import { annotateAndLogError } from '../error';
import Cache, { NO_RESULT_FROM_CACHE } from './cache';

const SSR_DEFAULT_TIMEOUT = 2000;
const SSR_DEFAULT_RETRIES = 1;
const CSR_DEFAULT_RETRIES = 1;

axiosRetry(axios, {
  shouldResetTimeout: true
});

/**
 * Publish an event with the needed metadata to "track" a
 * component request
 * @param {*} requestPromise
 * @param {*} requestConfig
 * @param {*} options
 */
const publishRequestEvent = (requestPromise, requestConfig, options) => {
  let EventBusInstance = false;
  if (window && window.FR && window.FR.EventBus) {
    EventBusInstance = window.FR.EventBus;
  } else if (window && window.EM && window.EM.utils && window.EM.utils.PubSub) {
    EventBusInstance = { ...window.EM.utils.PubSub };
  }
  if (EventBusInstance) {
    const dataToBroadcast = {
      requestPromise,
      requestConfig,
      options
    };
    EventBusInstance.publish('fr.request', dataToBroadcast);
  }
};

/**
 * Validator for the axiosWrapper interface
 * @param {*} options
 */
const validateWrapperOptions = (options) => {
  const validation = { isValid: true, message: '' };
  if (!options || !options.integrationSettings) {
    validation.isValid = false;
    validation.message = 'An *integrationSettings* object is mandatory';
  }
  if (!options || !options.meta) {
    validation.isValid = false;
    validation.message = 'An *options.meta* object is mandatory';
  }
  if (!options || !options.resource) {
    validation.isValid = false;
    validation.message = 'An *options.resource* string is mandatory';
  }
  return validation;
};

const getResponseFromCacheEm = (cacheKey) => {
  let responseFromCache;
  if (typeof window !== 'undefined') {
    if (!window.EM) {
      window.EM = {};
    }
    if (!window.EM.___Cache) {
      window.EM.___Cache = {};
    }
    if (window.EM.___Cache[cacheKey]) {
      responseFromCache = window.EM.___Cache[cacheKey];
    }
  }
  return responseFromCache;
};

const saveResponseToCacheEm = (cacheKey, response) => {
  if (typeof window !== 'undefined') {
    if (!window.EM) {
      window.EM = {};
    }
    if (!window.EM.___Cache) {
      window.EM.___Cache = {};
    }
    window.EM.___Cache[cacheKey] = response;
  }
  return response;
};

/**
 * Allows to add common functionality such as timeouts
 * for SSR to all requests that are being made
 * @param {*} requestConfig
 * @param {*} options
 */
const cache = new Cache();
const axiosWrapper = (requestConfig, options) => {
  const ssr = isSSR(options.simulateSSR);
  // const requestCacheKey = cache.genObjectKey({ requestConfig, options });
  const cacheKey = cache.getCacheKey(requestConfig.url, requestConfig.data);
  return new Promise((resolve, reject) => {
    // CHECK CACHE
    if (options.cache) {
      // CHECK CACHE EM
      if (typeof window !== 'undefined') {
        var cacheResult = getResponseFromCacheEm(cacheKey);
        if (cacheResult) {
          return resolve(cacheResult);
        }
      }
      // CHECK CACHE
      const cachedResponse = cache.get(cacheKey);
      if (cachedResponse !== NO_RESULT_FROM_CACHE) {
        return resolve(cachedResponse);
      }
    }

    const requestConfigCopy = Object.assign({}, requestConfig);

    const validation = validateWrapperOptions(options);
    const { resource, integrationSettings, meta, pageContext } = options;
    if (!validation.isValid) {
      logger.error(validation.message);
      return reject(validation);
    }

    let defaultRetries;
    let defaultTimeout;
    const retryConfig = {
      retryCondition: (error) => {
        // TODO: axios-retry doesn't have a way to hook into their process
        // If we use a different interceptor we will not be able to access the error
        // which is already consumed by axios-retry.
        // This is an ugly hack, we are hooking into the retry condition, which
        // is called once and has access to the error.
        // https://github.com/softonic/axios-retry/issues/75#issuecomment-502151719
        // https://github.com/softonic/axios-retry/issues/35
        const enhancedResource = `API_RETRY -> ${resource}`;
        annotateAndLogError(
          error,
          enhancedResource,
          false,
          false,
          integrationSettings,
          meta,
          pageContext
        );
        return isRetryableError(error); // Retry only on 500 errors
      }
    };
    if (ssr) {
      defaultRetries = isNotNil(integrationSettings.ssrRetries)
        ? integrationSettings.ssrRetries
        : SSR_DEFAULT_RETRIES;
      defaultTimeout = isNotNil(integrationSettings.ssrTimeout)
        ? integrationSettings.ssrTimeout
        : SSR_DEFAULT_TIMEOUT;
    } else {
      defaultRetries = isNotNil(integrationSettings.csrRetries)
        ? integrationSettings.csrRetries
        : CSR_DEFAULT_RETRIES;
      defaultTimeout = isNotNil(integrationSettings.csrTimeout)
        ? integrationSettings.csrTimeout
        : undefined;
    }
    if (isNotNil(defaultRetries)) {
      retryConfig.retries = defaultRetries;
    }
    if (isNotNil(defaultTimeout)) {
      retryConfig.timeout = defaultTimeout;
    }

    // add request-payload header with the reqPayload to improve traceability in SSR
    const reqPayload = requestConfigCopy && requestConfigCopy.data;
    if (ssr && reqPayload) {
      const payloadMaxCharacters = 2 * 1024;
      const strPayload = JSON.stringify(reqPayload);
      if (reqPayload && strPayload.length <= payloadMaxCharacters) {
        requestConfigCopy.headers = Object.assign(
          {},
          {
            'request-payload': JSON.stringify(reqPayload)
          },
          requestConfigCopy.headers || {}
        );
      }
    }

    // merge common headers from integrationSettings to axiosConfig
    requestConfigCopy.headers = Object.assign(
      {},
      integrationSettings.headers || {},
      requestConfigCopy.headers || {}
    );

    const requestPromise = axios({ ...requestConfigCopy, 'axios-retry': retryConfig })
      .then((response) => {
        if (options.cache) {
          //save cache EM
          saveResponseToCacheEm(cacheKey, response);
          cache.set(cacheKey, response, options.cacheDuration || -1);
        }
        resolve(response);
      })
      .catch((err) => {
        const enhancedResource = `API_REQUEST -> ${resource}`;
        const error = annotateAndLogError(
          err,
          enhancedResource,
          false,
          false,
          integrationSettings,
          meta,
          pageContext
        );
        reject(error);
      });

    // publish and event with the promise and the request config
    if (!isSSR()) {
      publishRequestEvent(requestPromise, requestConfigCopy, options);
    }
    return true;
  });
};

export default axiosWrapper;
